import { Download, Share, Smartphone, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

const INSTALL_READY_EVENT = "cofreweb-install-ready";
const isIosDevice = () => (
  /iPad|iPhone|iPod/.test(window.navigator.userAgent)
  || (window.navigator.userAgent.includes("Macintosh") && window.navigator.maxTouchPoints > 1)
);

const isStandalone = () => (
  window.matchMedia("(display-mode: standalone)").matches
  || window.navigator.standalone === true
);

export const InstallAppButton = ({ mobile = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [ios, setIos] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    setIos(isIosDevice());
    setInstalled(isStandalone());

    const loadInstallPrompt = () => {
      setDeferredPrompt(window.cofrewebDeferredInstallPrompt || null);
    };

    const markInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setShowIosHelp(false);
      window.cofrewebDeferredInstallPrompt = null;
    };

    loadInstallPrompt();
    window.addEventListener(INSTALL_READY_EVENT, loadInstallPrompt);
    window.addEventListener("appinstalled", markInstalled);

    return () => {
      window.removeEventListener(INSTALL_READY_EVENT, loadInstallPrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  useEffect(() => {
    if (!showIosHelp) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowIosHelp(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [showIosHelp]);

  const install = async () => {
    if (ios) {
      setShowIosHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    window.cofrewebDeferredInstallPrompt = null;
    if (choice.outcome === "accepted") {
      toast.success("CofreWeb foi adicionada ao seu aparelho.");
    }
  };

  if (installed || (!ios && !deferredPrompt)) return null;

  const buttonClass = mobile
    ? "flex min-h-[52px] items-center gap-3 rounded-xl bg-emerald-50 px-3 text-lg font-bold "
      + "text-emerald-900 transition-colors hover:bg-emerald-100"
    : "inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-emerald-200 "
      + "bg-white px-3 text-base font-bold text-emerald-800 "
      + "transition-colors hover:bg-emerald-50";

  return (
    <>
      <button
        type="button"
        onClick={install}
        data-testid={mobile ? "mobile-install-app-button" : "install-app-button"}
        className={buttonClass}
      >
        <Download className="h-5 w-5" />
        Instalar app
      </button>
      {showIosHelp && createPortal(
        <div
          data-testid="ios-install-dialog"
          className="fixed inset-0 z-50 grid place-items-center bg-emerald-950/60 p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ios-install-title"
          onClick={() => setShowIosHelp(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div
                className={[
                  "grid h-12 w-12 shrink-0 place-items-center rounded-2xl",
                  "bg-emerald-700 text-amber-200",
                ].join(" ")}
              >
                <Smartphone className="h-6 w-6" />
              </div>
              <button
                type="button"
                onClick={() => setShowIosHelp(false)}
                aria-label="Fechar"
                data-testid="ios-install-close-button"
                className={[
                  "grid h-11 w-11 place-items-center rounded-xl text-emerald-900",
                  "hover:bg-emerald-50",
                ].join(" ")}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <h2
              id="ios-install-title"
              data-testid="ios-install-title"
              className="mt-5 font-heading text-3xl font-extrabold text-emerald-950"
            >
              Instalar CofreWeb
            </h2>
            <ol className="mt-5 space-y-3 text-base leading-relaxed text-slate-700">
              <li className="flex gap-3">
                <span className="font-bold text-emerald-800">1.</span>
                <span>Toque em <strong>Compartilhar</strong> no Safari.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-emerald-800">2.</span>
                <span>Escolha <strong>Adicionar à Tela de Início</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-emerald-800">3.</span>
                <span>Confirme em <strong>Adicionar</strong>.</span>
              </li>
            </ol>
            <div
              className={[
                "mt-5 flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm",
                "font-semibold text-amber-900",
              ].join(" ")}
            >
              <Share className="h-5 w-5 shrink-0" />
              Abra esta página pelo Safari para instalar no iPhone.
            </div>
          </div>
        </div>
        ,
        document.body,
      )}
    </>
  );
};